import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Plus, UserPlus, Calendar, Users, FileText, X } from "lucide-react";

// Routes where the FAB should NOT appear
const HIDDEN_PATHS = [
  "/",
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
];

function startsWithAny(path, arr) {
  return arr.some((p) => path === p || path.startsWith(p + "/"));
}

export default function FloatingActionButton() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Listen for FAB-driven custom events so individual pages can react (e.g. open dialogs)
  // Pages register via window.addEventListener('hedesk:fab', cb)
  useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Close menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Visibility gate
  if (!user) return null;
  const path = location.pathname;
  if (startsWithAny(path, HIDDEN_PATHS)) return null;
  // Microsite & public invoice are public; FAB inside AppLayout only renders for app routes anyway

  const dispatchFab = (action) => {
    window.dispatchEvent(new CustomEvent("hedesk:fab", { detail: { action } }));
    setOpen(false);
  };

  // Define context-aware actions per route. Each action either:
  //  - navigates with state, OR
  //  - dispatches a hedesk:fab event that pages listen for to open their dialog
  const actions = [];
  // Walk-in (everyone)
  actions.push({
    key: "walkin",
    label: "Walk-in patient",
    icon: UserPlus,
    color: "bg-purple-600",
    onClick: () => {
      if (path.startsWith("/appointments")) {
        dispatchFab("walkin");
      } else {
        navigate("/appointments", { state: { openWalkIn: true } });
      }
    },
  });
  // New appointment
  actions.push({
    key: "appointment",
    label: "New appointment",
    icon: Calendar,
    color: "bg-blue-600",
    onClick: () => {
      if (path.startsWith("/appointments")) {
        dispatchFab("book");
      } else {
        navigate("/appointments", { state: { openBook: true } });
      }
    },
  });
  // New patient
  actions.push({
    key: "patient",
    label: "New patient",
    icon: Users,
    color: "bg-sky-600",
    onClick: () => {
      if (path.startsWith("/patients") && !path.match(/\/patients\/[^/]+$/)) {
        dispatchFab("newPatient");
      } else {
        navigate("/patients", { state: { openNewPatient: true } });
      }
    },
  });
  // New invoice — only on the patient detail page (where it can attach to a patient)
  if (path.match(/^\/patients\/[^/]+$/)) {
    actions.push({
      key: "visit",
      label: "New visit + invoice",
      icon: FileText,
      color: "bg-emerald-600",
      onClick: () => dispatchFab("newVisit"),
    });
  }

  return (
    <div
      ref={rootRef}
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 md:bottom-8 md:right-8"
      data-testid="fab-root"
    >
      {open && (
        <div className="flex flex-col items-end gap-2 mb-1 fade-in">
          {actions.map((a) => (
            <button
              key={a.key}
              onClick={a.onClick}
              data-testid={`fab-${a.key}`}
              className="group flex items-center gap-3 pr-1 pl-4 py-1 rounded-full bg-white shadow-lg border border-slate-200 hover:border-blue-300 hover:shadow-xl transition-all"
            >
              <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 whitespace-nowrap">
                {a.label}
              </span>
              <span className={`w-9 h-9 rounded-full ${a.color} text-white flex items-center justify-center shadow-sm`}>
                <a.icon className="w-4 h-4" strokeWidth={2} />
              </span>
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="fab-toggle"
        aria-label={open ? "Close quick actions" : "Open quick actions"}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center text-white transition-all ${
          open ? "bg-slate-700 rotate-45" : "bg-blue-600 hover:bg-blue-700 hover:scale-105"
        }`}
      >
        {open ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" strokeWidth={2.5} />}
      </button>
    </div>
  );
}
